import { useState, useEffect } from 'react';
import { Image as ImageIcon } from 'lucide-react';

/**
 * ProductImage — Reusable image component for product thumbnails/photos
 * across the app. Handles:
 *   - null/undefined/empty `src` → placeholder icon
 *   - failed load (broken URL, expired CDN, dead unsplash) → placeholder icon
 *   - resets error state when `src` changes (list virtualization safe)
 *
 * Props:
 *   src        — image URL (any protocol http/https/data:/blob:)
 *   alt        — accessible alt text
 *   className  — passed to <img> or placeholder wrapper
 *   iconSize   — placeholder icon size, defaults to h-6 w-6
 *   testid     — optional data-testid
 */
export function ProductImage({ src, alt = '', className = '', iconSize = 'h-6 w-6', testid }) {
  const [errored, setErrored] = useState(false);

  // Reset error state when src changes so a re-render with a new URL retries.
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const showPlaceholder = !src || errored;

  if (showPlaceholder) {
    return (
      <div
        data-testid={testid}
        className={`flex items-center justify-center bg-slate-100 text-slate-400 ${className}`}
        aria-label={alt || 'Sin imagen de producto'}
      >
        <ImageIcon className={iconSize} strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <img
      data-testid={testid}
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setErrored(true)}
      className={className}
    />
  );
}

export default ProductImage;
